import { router } from '../app/router';
import { authStore } from '../modules/auth/auth.store';

/* ------------------------------------------------------------------ */
// Constants
/* ------------------------------------------------------------------ */

const ROUTES = {
  HOME: '/',
  DISCOVER: '/discover',
  AUTH: '/auth',
  MEMBERSHIP: '/membership',
  SAFETY: '/safety',
  PRIVACY: '/privacy',
  TERMS: '/terms',
  GUIDELINES: '/guidelines',
} as const;

/* ------------------------------------------------------------------ */
// Types
/* ------------------------------------------------------------------ */

interface AuthStore {
  isAuthenticated: boolean;
}

interface LandingPageProps {
  container: HTMLElement;
  // Optional – if not provided, uses the global authStore
  isAuthenticated?: boolean;
}

interface TrustMetric {
  icon: string;
  label: string;
  sub: string;
}

/* ------------------------------------------------------------------ */
// Data
/* ------------------------------------------------------------------ */

const TRUST_METRICS: TrustMetric[] = [
  { icon: 'fa-user-check', label: 'Verified Profiles', sub: 'Photo & ID reviewed' },
  { icon: 'fa-microphone', label: 'Voice Intros', sub: 'Hear before you connect' },
  { icon: 'fa-brands fa-whatsapp', label: 'WhatsApp Ready', sub: 'Direct premium unlock' },
  { icon: 'fa-shield-halved', label: 'Active Moderation', sub: '24-hr safety team' },
];

interface DummyProfile {
  name: string;
  age: number;
  city: string;
  tribe: string;
  religion: string;
  intent: string;
  bio: string;
  photoUrl: string;
  initials: string;
  color: string;
  verified: boolean;
}

const FEATURES = [
  {
    icon: 'fa-id-badge',
    title: 'Verified Profiles',
    body: 'Every profile undergoes photo review and account screening before appearing in discovery.',
  },
  {
    icon: 'fa-brands fa-whatsapp',
    title: 'WhatsApp Unlock',
    body: 'Subscribe to unlock direct WhatsApp contact lines and skip the waiting game.',
  },
  {
    icon: 'fa-volume-high',
    title: 'Voice Note Intros',
    body: 'Hear how your potential match sounds before you swipe or send a message.',
  },
  {
    icon: 'fa-people-roof',
    title: 'Tribe & Religion Filters',
    body: 'Filter by tribe, religion, relationship intent, and city to find your match.',
  },
];

const DUMMY_PROFILES: DummyProfile[] = [
  {
    name: 'Adaeze Okafor',
    age: 26,
    city: 'Lagos',
    tribe: 'Igbo',
    religion: 'Christian',
    intent: 'Serious Relationship',
    bio: 'Lawyer by day, foodie by night. Looking for someone who can match my energy and love for adventures.',
    photoUrl: 'https://randomuser.me/api/portraits/women/44.jpg',
    initials: 'AO',
    color: '#9333ea',
    verified: true,
  },
  {
    name: 'Tunde Balogun',
    age: 30,
    city: 'Abuja',
    tribe: 'Yoruba',
    religion: 'Muslim',
    intent: 'Marriage',
    bio: 'Tech entrepreneur building the future. I value honesty, family, and good jollof rice debates.',
    photoUrl: 'https://randomuser.me/api/portraits/men/22.jpg',
    initials: 'TB',
    color: '#2563eb',
    verified: true,
  },
  {
    name: 'Fatima Ibrahim',
    age: 24,
    city: 'Kano',
    tribe: 'Hausa',
    religion: 'Muslim',
    intent: 'Marriage',
    bio: 'Medical student with a passion for poetry and helping others. Family is everything.',
    photoUrl: 'https://randomuser.me/api/portraits/women/68.jpg',
    initials: 'FI',
    color: '#dc2626',
    verified: true,
  },
  {
    name: 'Emeka Nwachukwu',
    age: 28,
    city: 'Port Harcourt',
    tribe: 'Igbo',
    religion: 'Christian',
    intent: 'Serious Relationship',
    bio: 'Oil & gas engineer who loves football, road trips, and deep conversations under the stars.',
    photoUrl: 'https://randomuser.me/api/portraits/men/54.jpg',
    initials: 'EN',
    color: '#059669',
    verified: false,
  },
  {
    name: 'Blessing Edet',
    age: 25,
    city: 'Calabar',
    tribe: 'Efik',
    religion: 'Christian',
    intent: 'Casual Dating',
    bio: 'Makeup artist and beauty influencer. I believe in good vibes, great food, and genuine connections.',
    photoUrl: 'https://randomuser.me/api/portraits/women/33.jpg',
    initials: 'BE',
    color: '#d97706',
    verified: true,
  },
  {
    name: 'Yusuf Adeyemi',
    age: 32,
    city: 'Ibadan',
    tribe: 'Yoruba',
    religion: 'Muslim',
    intent: 'Marriage',
    bio: 'Architect with a love for design, travel, and photography. Looking for my forever partner.',
    photoUrl: 'https://randomuser.me/api/portraits/men/47.jpg',
    initials: 'YA',
    color: '#7c3aed',
    verified: false,
  },
  {
    name: 'Chiamaka Eze',
    age: 27,
    city: 'Enugu',
    tribe: 'Igbo',
    religion: 'Christian',
    intent: 'Serious Relationship',
    bio: 'Software developer and part-time baker. Looking for someone genuine who appreciates the little things.',
    photoUrl: 'https://randomuser.me/api/portraits/women/11.jpg',
    initials: 'CE',
    color: '#0891b2',
    verified: true,
  },
  {
    name: 'Abdul Mohammed',
    age: 29,
    city: 'Kaduna',
    tribe: 'Hausa',
    religion: 'Muslim',
    intent: 'Friendship',
    bio: 'Pilot who loves exploring new cultures. Looking for meaningful connections, not games.',
    photoUrl: 'https://randomuser.me/api/portraits/men/62.jpg',
    initials: 'AM',
    color: '#b91c1c',
    verified: false,
  },
];

const FOOTER_NAV = [
  { label: 'Home', route: ROUTES.HOME },
  { label: 'Browse', route: ROUTES.DISCOVER },
  { label: 'Membership', route: ROUTES.MEMBERSHIP },
  { label: 'Safety', route: ROUTES.SAFETY },
];

const FOOTER_LEGAL = [
  { label: 'Privacy Policy', route: ROUTES.PRIVACY },
  { label: 'Terms of Service', route: ROUTES.TERMS },
  { label: 'Community Guidelines', route: ROUTES.GUIDELINES },
];

/* ------------------------------------------------------------------ */
// Helpers
/* ------------------------------------------------------------------ */

function escapeHtml(unsafe: string): string {
  return unsafe
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Navigate to a route with error handling.
 */
function navigateTo(route: string): void {
  try {
    router.navigate(route);
  } catch (error) {
    console.error(`Navigation to "${route}" failed:`, error);
    // Optionally show a user-friendly message (e.g., toast notification)
  }
}

/* ------------------------------------------------------------------ */
// Sections
/* ------------------------------------------------------------------ */

function renderHero(isAuth: boolean): string {
  const ctaLabel = isAuth ? 'Browse Profiles' : 'Join Zuri Now';
  const ctaRoute = isAuth ? ROUTES.DISCOVER : ROUTES.AUTH;
  const ctaIcon = isAuth ? 'fa-compass' : 'fa-user-plus';

  return `
    <section class="section pt-nav text-center" aria-labelledby="hero-heading">
      <div class="container max-w-lg">
        <div class="badge badge-accent mb-4">
          <i class="fa-solid fa-sparkles" aria-hidden="true"></i>
          Nigeria's Premium Connection Platform
        </div>
        <h1 id="hero-heading" class="mb-6">
          Discover Your <span class="gradient-text">Perfect Match</span>
        </h1>
        <p class="text-lg mb-8 max-w-md mx-auto text-muted">
          Browse verified profiles, listen to authentic voice intros, and unlock direct WhatsApp connections with genuine people.
        </p>

        <div class="flex items-center justify-center gap-4 flex-wrap mb-12">
          <button 
            class="btn btn-primary btn-lg" 
            data-route="${ctaRoute}"
            aria-label="${escapeHtml(ctaLabel)}"
          >
            <i class="fa-solid ${ctaIcon}" aria-hidden="true"></i> ${escapeHtml(ctaLabel)}
          </button>
          ${!isAuth ? `
            <button 
              class="btn btn-secondary btn-lg" 
              data-route="${ROUTES.MEMBERSHIP}"
              aria-label="View membership plans"
            >
              <i class="fa-solid fa-crown" aria-hidden="true"></i> View Plans
            </button>
          ` : ''}
        </div>

        <!-- Trust Metrics -->
        <div class="grid grid-4 gap-6 mt-12" role="list" aria-label="Platform trust indicators">
          ${TRUST_METRICS.map(m => `
            <div class="card card-body text-center" role="listitem">
              <i class="fa-solid ${m.icon} text-2xl mb-2 text-accent" aria-hidden="true"></i>
              <div class="text-xl font-bold">${escapeHtml(m.label)}</div>
              <div class="text-sm text-muted">${escapeHtml(m.sub)}</div>
            </div>
          `).join('')}
        </div>
      </div>
    </section>
  `;
}

function renderFeatures(): string {
  return `
    <section class="section" aria-labelledby="features-heading">
      <div class="container">
        <div class="section-header text-center">
          <h2 id="features-heading">Why Singles Choose <span class="gradient-text">Zuri</span></h2>
          <p class="section-subtitle text-muted">Designed for safety, cultural intent, and seamless messaging.</p>
        </div>
        <div class="feature-grid">
          ${FEATURES.map(f => `
            <article class="card card-body text-center">
              <div class="text-3xl mb-4 text-accent" aria-hidden="true">
                <i class="fa-solid ${f.icon}"></i>
              </div>
              <h3 class="mb-2">${escapeHtml(f.title)}</h3>
              <p class="text-muted">${escapeHtml(f.body)}</p>
            </article>
          `).join('')}
        </div>
      </div>
    </section>
  `;
}

function renderProfileCard(p: DummyProfile): string {
  return `
    <article class="profile-card">
      <div class="profile-card-image" style="cursor:default">
        <img src="${escapeHtml(p.photoUrl)}" alt="${escapeHtml(p.name)}" loading="lazy" style="width:100%;height:100%;object-fit:cover" />
        ${p.verified ? '<span class="badge badge-verified" style="position:absolute;top:12px;right:12px"><i class="fa-solid fa-circle-check"></i> Verified</span>' : ''}
      </div>
      <div class="profile-card-body">
        <div class="name-age">${escapeHtml(p.name)}, ${p.age}</div>
        <div class="location"><i class="fa-solid fa-location-dot"></i> ${escapeHtml(p.city)}</div>
        <div class="flex gap-2 mt-2" style="flex-wrap:wrap">
          <span class="tag"><i class="fa-solid fa-hand-holding-heart"></i> ${escapeHtml(p.intent)}</span>
          <span class="tag"><i class="fa-solid fa-people-group"></i> ${escapeHtml(p.tribe)}</span>
          <span class="tag"><i class="fa-solid fa-mosque"></i> ${escapeHtml(p.religion)}</span>
        </div>
        <p class="text-sm mt-2" style="color:var(--color-text-muted)">${escapeHtml(p.bio.substring(0, 110))}${p.bio.length > 110 ? '...' : ''}</p>
        <div class="mt-4">
          <button class="btn btn-primary w-full" data-route="${ROUTES.AUTH}" style="font-size:14px;padding:10px 16px">
            <i class="fa-solid fa-user-plus"></i> Join to Connect
          </button>
        </div>
      </div>
    </article>
  `;
}

function renderFeaturedProfiles(): string {
  // Rotate the two spotlights once per ISO-style week so the landing page
  // remains fresh without exposing private profile data to signed-out guests.
  const week = Math.floor(Date.now() / (7 * 24 * 60 * 60 * 1000));
  const women = DUMMY_PROFILES.filter((profile) => profile.name !== 'Tunde Balogun' && profile.name !== 'Emeka Nwachukwu' && profile.name !== 'Yusuf Adeyemi' && profile.name !== 'Abdul Mohammed');
  const men = DUMMY_PROFILES.filter((profile) => !women.includes(profile));
  const spotlight = [women[week % women.length], men[week % men.length]];
  return `
    <section class="section" aria-labelledby="featured-heading">
      <div class="container">
        <div class="section-header text-center">
          <div class="badge badge-accent mb-2"><i class="fa-solid fa-star"></i> This Week's Spotlight</div>
          <h2 id="featured-heading">Woman &amp; Man <span class="gradient-text">of the Week</span></h2>
          <p class="section-subtitle text-muted">Meet a fresh pair of community spotlights each week. Join to browse verified members.</p>
        </div>
        <div class="grid grid-2 gap-6">
          ${spotlight.map(p => renderProfileCard(p)).join('')}
        </div>
      </div>
    </section>
  `;
}

function renderCta(isAuth: boolean): string {
  if (isAuth) return '';

  return `
    <section class="section" aria-labelledby="cta-heading">
      <div class="container max-w-md text-center">
        <div class="card card-body border-accent shadow-glow">
          <div class="badge badge-accent mb-4">
            <i class="fa-solid fa-heart-pulse" aria-hidden="true"></i> Ready to Meet Someone?
          </div>
          <h2 id="cta-heading" class="mb-4">Join Zuri Today</h2>
          <p class="mb-6 text-muted">
            Create your profile, upload your photos, record your voice intro, and get discovered.
          </p>
          <button 
            class="btn btn-primary btn-lg w-full shadow-glow" 
            data-route="${ROUTES.AUTH}"
            aria-label="Create a free Zuri account"
          >
            <i class="fa-solid fa-user-plus" aria-hidden="true"></i> Join Zuri Now
          </button>
          <p class="text-xs text-muted mt-4">
            <i class="fa-solid fa-shield-halved" aria-hidden="true"></i> 
            Safety-first: all photos are reviewed before going live. 🔞 18+ only.
          </p>
        </div>
      </div>
    </section>
  `;
}

function renderFooter(): string {
  return `
    <footer class="footer">
      <div class="container flex justify-between gap-8 flex-wrap">
        <div class="max-w-sm">
          <div class="brand mb-4">
            <div class="brand-icon" aria-hidden="true"><i class="fa-solid fa-heart"></i></div>
            <span>Zuri</span>
          </div>
          <p class="text-muted">Nigeria's premium dating and connection platform. Connecting hearts with safety and intent.</p>
        </div>
        <nav aria-label="Footer navigation">
          <h4 class="mb-4">Navigation</h4>
          <ul class="flex flex-col gap-2 list-none p-0">
            ${FOOTER_NAV.map(n => `
              <li>
                <a 
                  href="#${n.route}" 
                  class="text-sm text-secondary hover:text-accent transition-colors"
                  data-route="${n.route}"
                >${escapeHtml(n.label)}</a>
              </li>
            `).join('')}
          </ul>
        </nav>
        <nav aria-label="Legal links">
          <h4 class="mb-4">Legal</h4>
          <ul class="flex flex-col gap-2 list-none p-0">
            ${FOOTER_LEGAL.map(l => `
              <li>
                <a 
                  href="#${l.route}" 
                  class="text-sm text-secondary hover:text-accent transition-colors"
                  data-route="${l.route}"
                >${escapeHtml(l.label)}</a>
              </li>
            `).join('')}
          </ul>
        </nav>
      </div>
      <div class="container text-center mt-8 pt-8 border-top">
        <p class="text-sm text-muted">© ${new Date().getFullYear()} Zuri. All rights reserved. 🔞 18+ only.</p>
      </div>
    </footer>
  `;
}

/* ------------------------------------------------------------------ */
// Main
/* ------------------------------------------------------------------ */

export function renderLandingPage(container: HTMLElement): () => void {
  // Determine authentication state from the global auth store
  const isAuth = authStore?.isAuthenticated ?? false;

  // Render the entire page
  container.innerHTML = `
    <main>
      ${renderHero(isAuth)}
      ${renderFeaturedProfiles()}
      ${renderFeatures()}
      ${renderCta(isAuth)}
      ${renderFooter()}
    </main>
  `;

  // Use AbortController for unified cleanup
  const abortController = new AbortController();
  const { signal } = abortController;

  // ----- Event Delegation: Click navigation -----
  container.addEventListener(
    'click',
    (e) => {
      const target = e.target as HTMLElement;
      const routeElement = target.closest('[data-route]') as HTMLElement | null;
      if (!routeElement) return;

      e.preventDefault();
      const route = routeElement.getAttribute('data-route');
      if (route) navigateTo(route);
    },
    { signal }
  );

  // ----- Keyboard support: Enter/Space on interactive elements -----
  container.addEventListener(
    'keydown',
    (e) => {
      const key = e.key;
      if (key !== 'Enter' && key !== ' ') return;

      const target = e.target as HTMLElement;
      const interactive = target.closest('button, a[data-route]');
      if (!interactive) return;

      // Prevent default scrolling for Space
      e.preventDefault();
      (interactive as HTMLElement).click();
    },
    { signal }
  );

  // ----- Cleanup -----
  return function unmount() {
    abortController.abort(); // removes all listeners added with this signal
    container.innerHTML = ''; // clear DOM
  };
}
